import React, { useMemo } from 'react';
import { BarChart, TrendingUp, Droplet, BarChart2 } from 'lucide-react';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент для визуализации результатов расчета расхода воды
 */
const FlowRateVisualization = ({ results, formData }) => {
  // Безопасное использование useMemo - хуки вызываются в каждом рендере
  // и не зависят от условий
  const velocityPercentage = useMemo(() => {
    if (!results) return 0;
    // Нормализуем значение скорости (0-10 м/с) до процентов (0-100%)
    const normalizedVelocity = Math.min(Math.max(results.velocity || 0, 0), 10);
    return (normalizedVelocity / 10) * 100;
  }, [results]);

  // Расчет процента заполнения для визуализации расхода
  const flowRatePercentage = useMemo(() => {
    if (!results) return 0;
    // Определяем максимальный расход в зависимости от диаметра
    const diameter = results.pipeInnerDiameter || 0; // мм
    // Примерная максимальная пропускная способность трубы данного диаметра при 3 м/с
    const maxFlowRate = Math.PI * Math.pow(diameter/1000/2, 2) * 3 * 3600; // м³/ч
    
    // Нормализуем значение расхода до процентов (0-100%)
    const flowRate = !results ? 0 :
                     results.calculationType === 'flowRate' ? results.flowRate : 
                     results.calculationType === 'pressure' && formData ? parseFloat(formData.flowRate) || 0 :
                     results.calculationType === 'diameter' && formData ? parseFloat(formData.requiredFlowRate) || 0 : 0;
                     
    return maxFlowRate > 0 ? Math.min((flowRate / maxFlowRate) * 100, 100) : 0;
  }, [results, formData]);

  // Если нет результатов, ничего не отображаем
  if (!results) return null;
  
  // Определяем цвет для скорости потока на основе рекомендуемых значений
  const getVelocityColor = () => {
    if (results.velocity < 0.5) return 'text-yellow-500 dark:text-yellow-400'; // Слишком низкая
    if (results.velocity > 5) return 'text-red-500 dark:text-red-400'; // Слишком высокая
    if (results.velocity > 3) return 'text-orange-500 dark:text-orange-400'; // Высокая
    return 'text-green-500 dark:text-green-400'; // Оптимальная
  };

  // Определяем стиль заполнения для скорости
  const velocityBarStyle = {
    width: `${velocityPercentage}%`,
    backgroundColor: results.velocity < 0.5 ? '#EAB308' : 
                    results.velocity > 5 ? '#EF4444' : 
                    results.velocity > 3 ? '#F97316' : 
                    '#22C55E'
  };

  // Определяем стиль заполнения для расхода
  const flowRateBarStyle = {
    width: `${flowRatePercentage}%`,
    backgroundColor: '#3B82F6'
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <BarChart2 className="w-5 h-5 mr-2 text-blue-500" />
        Визуализация параметров потока
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Визуализация скорости потока */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <div className="flex items-center">
              <TrendingUp className="w-4 h-4 mr-1.5 text-blue-500" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Скорость потока</span>
            </div>
            <span className={`text-sm font-bold ${getVelocityColor()}`}>
              {formatNumber(results.velocity, 2)} м/с
            </span>
          </div>
          
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4 mb-1">
            <div 
              className="h-4 rounded-full transition-all duration-500"
              style={velocityBarStyle}
            ></div>
          </div>
          
          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
            <span>Медленно (0 м/с)</span>
            <span className="text-yellow-500 dark:text-yellow-400">0.5</span>
            <span className="text-green-500 dark:text-green-400">3</span>
            <span className="text-red-500 dark:text-red-400">5</span>
            <span>Быстро (10 м/с)</span>
          </div>
          
          <div className="mt-3 text-xs text-gray-600 dark:text-gray-400">
            {results.velocity < 0.5 && (
              <p className="text-yellow-600 dark:text-yellow-400">
                ⚠️ Слишком низкая скорость. Возможны отложения в трубе.
              </p>
            )}
            {results.velocity > 0.5 && results.velocity <= 3 && (
              <p className="text-green-600 dark:text-green-400">
                ✓ Оптимальная скорость потока.
              </p>
            )}
            {results.velocity > 3 && results.velocity <= 5 && (
              <p className="text-orange-600 dark:text-orange-400">
                ⚠️ Повышенная скорость. Возможен шум в трубопроводе.
              </p>
            )}
            {results.velocity > 5 && (
              <p className="text-red-600 dark:text-red-400">
                ⚠️ Высокая скорость! Риск эрозии трубы и кавитации.
              </p>
            )}
          </div>
        </div>

        {/* Визуализация расхода */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <div className="flex items-center">
              <Droplet className="w-4 h-4 mr-1.5 text-blue-500" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Расход воды</span>
            </div>
            <span className="text-sm font-bold text-blue-600 dark:text-blue-400">
              {formatNumber(results.calculationType === 'flowRate' ? results.flowRate : 
                results.calculationType === 'pressure' ? parseFloat(formData.flowRate) :
                results.calculationType === 'diameter' ? parseFloat(formData.requiredFlowRate) : 0, 2)} м³/ч
            </span>
          </div>
          
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4 mb-1">
            <div 
              className="h-4 rounded-full bg-blue-500 transition-all duration-500"
              style={flowRateBarStyle}
            ></div>
          </div>
          
          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
            <span>0 м³/ч</span>
            <span>25%</span>
            <span>50%</span>
            <span>75%</span>
            <span>100%</span>
          </div>
          
          <div className="mt-3 text-xs text-gray-600 dark:text-gray-400">
            <p>
              Расход показан относительно максимальной пропускной способности трубы 
              диаметром {formatNumber(results.pipeInnerDiameter, 1)} мм при скорости потока 3 м/с.
            </p>
          </div>
        </div>
      </div>

      {/* Информация о режиме потока */}
      <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700/40 rounded-lg border border-gray-200 dark:border-gray-700">
        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2 flex items-center">
          <BarChart className="w-4 h-4 mr-1.5 text-blue-500" />
          Характер потока (число Рейнольдса: {formatNumber(results.reynoldsNumber, 0)})
        </h4>
        
        {/* Шкала режимов течения */}
        <div className="relative w-full h-10 bg-gradient-to-r from-blue-500 via-yellow-500 to-red-500 rounded-lg mt-1 mb-3">
          {/* Маркер текущего значения */}
          <div 
            className="absolute top-0 w-1 h-10 bg-white dark:bg-gray-900 border-2 border-gray-800 dark:border-white"
            style={{ 
              left: `${Math.min(Math.max(results.reynoldsNumber / 10000 * 100, 0), 100)}%`, 
              transform: 'translateX(-50%)' 
            }}
          ></div>
          
          {/* Метки режимов */}
          <div className="absolute -bottom-6 left-0 text-xs text-blue-600 dark:text-blue-400">
            Ламинарный
            <br />
            &lt; 2300
          </div>
          <div className="absolute -bottom-6 left-1/4 text-xs text-yellow-600 dark:text-yellow-400 transform -translate-x-1/2">
            Переходный
            <br />
            2300-4000
          </div>
          <div className="absolute -bottom-6 left-1/2 text-xs text-orange-600 dark:text-orange-400 transform -translate-x-1/2">
            Турбулентный
            <br />
            &gt; 4000
          </div>
        </div>
        
        <div className="mt-6 text-sm text-gray-600 dark:text-gray-400">
          {results.flowType === 'Ламинарный' && (
            <p>
              <strong className="text-blue-600 dark:text-blue-400">Ламинарный поток</strong> — слои жидкости движутся параллельно друг другу без перемешивания. 
              Характерен низкими потерями давления, но и низкой теплопередачей.
            </p>
          )}
          {results.flowType === 'Переходный' && (
            <p>
              <strong className="text-yellow-600 dark:text-yellow-400">Переходный режим</strong> — нестабильное состояние между ламинарным и турбулентным. 
              Может быть непредсказуемым, с резкими изменениями давления и шумом.
            </p>
          )}
          {results.flowType === 'Турбулентный' && (
            <p>
              <strong className="text-orange-600 dark:text-orange-400">Турбулентный поток</strong> — характеризуется хаотичным движением частиц жидкости с интенсивным перемешиванием. 
              Обеспечивает хорошую теплопередачу, но имеет более высокие потери давления.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default FlowRateVisualization;